import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavLink,
  NavItem,
  TabContent,
  TabPane,
  Col,
  Row,
} from 'reactstrap';
import { SkeletonCard } from '../Common/Skeleton';
import IncotermTableFragment from './IncotermTableFragment';
import QuantityPricingFields from './QuantityPricingFields';
import useQuantityDeliveryTabs from './hooks/useQuantityDeliveryTabs';
import { displayValue } from './utils/quantityDeliveryUtils';

const QuantityColumn = ({
  parcel,
  isLpg = false,
  quantityUnitLabel = isLpg ? 'MT' : 'MB',
}) => {
  const quantities = parcel.quantities ?? [];

  return (
    <Col md={4}>
      <div className='text-muted small fw-semibold mb-2'>Quantity</div>
      <div className='border rounded shadow-none bg-white'>
        <div className='bg-light p-2 border-bottom text-muted small fw-medium fs-12'>
          {quantityUnitLabel}
        </div>

        {quantities.length > 0 ? (
          quantities.map((qty, index) => (
            <div
              key={qty.id}
              className={`p-2 d-flex justify-content-between align-items-center ${
                index !== quantities.length - 1 ? 'border-bottom' : ''
              }`}
            >
              <span className='text-dark fs-13'>
                {displayValue(qty.quantity)}
              </span>
            </div>
          ))
        ) : (
          <div className='text-center text-muted p-3 fs-13'>
            No quantity data available
          </div>
        )}
      </div>
    </Col>
  );
};

const QuantityDeliverySection = ({
  parcels = [],
  isLoading = false,
  error = null,
  isLpg = false,
  quantityUnitLabel = isLpg ? 'MT' : 'MB',
  emptyMessage = 'No quantity delivery data available.',
}) => {
  const { parcelTabs, toggleParcelTab } = useQuantityDeliveryTabs(parcels);

  if (isLoading) {
    return <SkeletonCard lines={5} className='border mb-4' />;
  }

  if (error) {
    return <div className='alert alert-danger mb-0'>{error}</div>;
  }

  if (!parcels.length) {
    return <p className='text-muted small mb-0'>{emptyMessage}</p>;
  }

  return parcels.map(parcel => {
    const activeTab = parcelTabs[parcel.id] || 'quantity';

    return (
      <Card key={parcel.id} className='border shadow-sm mb-4'>
        <CardHeader className='bg-white p-0 border-bottom'>
          <Nav tabs className='nav-tabs m-0'>
            <NavItem>
              <NavLink
                tag='button'
                type='button'
                className={
                  activeTab === 'quantity'
                    ? 'active text-primary fw-semibold text-uppercase py-3 px-4'
                    : 'text-uppercase fw-semibold text-muted py-3 px-4'
                }
                onClick={() => toggleParcelTab(parcel.id, 'quantity')}
                style={{ cursor: 'pointer' }}
              >
                Delivery Unit
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag='button'
                type='button'
                className={
                  activeTab === 'pricing'
                    ? 'active text-primary fw-semibold text-uppercase py-3 px-4'
                    : 'text-uppercase fw-semibold text-muted py-3 px-4'
                }
                onClick={() => toggleParcelTab(parcel.id, 'pricing')}
                style={{ cursor: 'pointer' }}
              >
                Pricing
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>

        <CardBody className='p-4'>
          <TabContent activeTab={activeTab}>
            <TabPane tabId='quantity'>
              <Row className='g-3 mb-4'>
                <Col md={6}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      Delivery Unit
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.deliveryUnit)}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      {isLpg ? 'Cargo Type' : 'Frequency'}
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {isLpg
                        ? displayValue(parcel.cargoTypeLabel)
                        : displayValue(parcel.frequency)}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className='g-3'>
                <QuantityColumn
                  parcel={parcel}
                  isLpg={isLpg}
                  quantityUnitLabel={quantityUnitLabel}
                />
                <IncotermTableFragment items={parcel.incotermTimelineItems} />
              </Row>
            </TabPane>

            <TabPane tabId='pricing'>
              <QuantityPricingFields parcel={parcel} />
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    );
  });
};

export default QuantityDeliverySection;
